import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import RichTextConverter from '../RichText/RichTextConverter';
import ScrollTo, { smoothScrollToRef } from '../ScrollTo';
import AdTest from './AdTest';
import FormContainer from './FormContainer';
import { loadFormCallback, whenReady, whenRenderedCallback } from './formEvents';
import * as styles from './MarketoForm.module.css';

const MarketoForm = ({ formId, followUpForms, onSuccess, ...rest }) => {
  const [step, setStep] = useState(0);
  const topRef = useRef(null);
  if (!followUpForms) {
    return <MarketoFormContainer formId={formId} onSuccess={onSuccess} {...rest} />;
  }
  let forms = followUpForms.split(',');
  forms.unshift(formId);

  const finalStep = forms.length - 1;
  const onStepSuccess = () => {
    if (step === finalStep) {
    } else {
      setStep(step + 1);
      smoothScrollToRef(topRef);
    }
  };

  let components = forms.map((id, i) => {
    return (
      <MarketoFormContainer
        splitName={rest.splitName}
        isNewDemo={rest.isNewDemo}
        key={i}
        formId={id}
        onSuccess={i === finalStep ? onSuccess : onStepSuccess}
        {...rest}
      />
    );
  });
  return (
    <div key={`current-step-${step}`}>
      <ScrollTo reference={topRef} />
      <div className={`current-step-${step}`} />
      {components[step]}
    </div>
  );
};

class MarketoFormContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      success: false,
      formLoaded: false,
      formUnavailable: false,
    };
    this._isMounted = false;
    this.onSuccess = this.onSuccess.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    if (!this.state.formLoaded) {
      this.scriptLoaded();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    clearInterval(this.intervalId);
  }

  onSuccess() {
    if (typeof window !== 'undefined' && !!window.FS) {
      window.FS.event('Marketo Form', { type: 'Form was successful', formID: this.props.formId.toString() });
      window['optimizely'] = window['optimizely'] || [];
      window['optimizely'].push({
        type: 'event',
        eventName: 'successful_demo_submission',
        tags: {},
      });
    }
    if (this.props.redirectUrl) {
      window.location = this.props.redirectUrl;
      return false;
    }

    if (this._isMounted) {
      this.setState({
        success: true,
      });
    }

    this.props.onSuccess && this.props.onSuccess();
    return false;
  }

  async scriptLoaded() {
    if (typeof window === 'undefined') {
      return;
    }
    let mktoReady = false;
    let checkTime = 500;
    let tries = 0;
    let maxTries = 20;

    const mktoCheck = () => {
      if (this._isMounted) {
        if (tries === maxTries && !window.MktoForms2) {
          clearInterval(this.intervalId);
          if (typeof window !== 'undefined' && !!window.FS) {
            window.FS.event('Marketo Form', {
              type: 'Form failed to load',
              formID: (this.props.formId && this.props.formId.toString()) || '',
            });
          }
          this.setState({ formUnavailable: true, formLoaded: true });
          return;
        }

        tries++;
        if (!!window.MktoForms2) {
          mktoReady = true;
          clearInterval(this.intervalId);
        }

        if (mktoReady && !this.state.formLoaded) {
          const { formId } = this.props;
          const start = performance.now();
          window.MktoForms2.loadForm('//app-ab31.marketo.com', '002-LDF-436', formId.toString(), (form) =>
            loadFormCallback(form, this._isMounted, this.onSuccess)
          )
            .whenReady(async (form) => {
              await whenReady(form, this.props.addHiddenFields, start);
              if (this._isMounted) {
                await this.setState({ formLoaded: true });
              }
            })
            .whenRendered(whenRenderedCallback);
        }
      }
    };
    this.intervalId = setInterval(mktoCheck, checkTime);
  }

  render() {
    if (this.state.formUnavailable) {
      return (
        <AdTest
          formId={this.props.formId}
          formLoadFailed={this.state.formUnavailable}
          className={this.props.adCheckClass}
        />
      );
    }

    if (this.state.success) {
      const { className, successMessage } = this.props;
      const defaultMessage = <span>Thank you! A team member will follow up within one business day.</span>;
      return (
        <div className={[className, styles.marketoFormSuccess, 'marketo-render-success'].join(' ')}>
          {successMessage && successMessage.raw ? (
            <RichTextConverter copy={successMessage} />
          ) : successMessage ? (
            <span>{successMessage}</span>
          ) : (
            defaultMessage
          )}
          {/*<FormSuccess image={successImage} title={successTitle} message={successMessage} />*/}
          {this.props.trackingPixelUrl && (
            <img
              className={'li-tr'}
              src={this.props.trackingPixelUrl}
              height="1"
              width="1"
              style={{ display: 'none' }}
              alt=""
            />
          )}
        </div>
      );
    }

    const fade = this.state.formLoaded ? styles.fadeIn : styles.fadeOut;
    return (
      <>
        {!this.state.formLoaded && !this.state.success && <span key="loading-spinner" className={styles.spinner} />}
        <FormContainer
          className={this.props.className}
          fade={fade}
          splitName={this.props.splitName}
          isNewDemo={this.props.isNewDemo}
          formId={this.props.formId}
        />
      </>
    );
  }
}

MarketoFormContainer.defaultProps = {
  splitName: true,
  finalStep: true,
};

MarketoForm.propTypes = {
  formId: PropTypes.string.isRequired,
  redirectUrl: PropTypes.string,
  className: PropTypes.string,
  successTitle: PropTypes.string,
  successImage: PropTypes.string,
  successMessage: PropTypes.any,
  onSuccess: PropTypes.func,
  addHiddenFields: PropTypes.bool,
  //Used for two column name fields
  splitName: PropTypes.bool,
  adCheckClass: PropTypes.string,
};

export default MarketoForm;
